/**
 * Calculate ELO rating change for a match result
 * @param ratingA - Current ELO rating of player/team A
 * @param ratingB - Current ELO rating of player/team B  
 * @param playerAWon - Whether player/team A won the match
 * @param kFactor - K-factor for ELO calculation (default: 40)
 * @returns ELO change for player A (player B gets the opposite)
 */
export function calculateEloChange(
  ratingA: number,
  ratingB: number,
  playerAWon: boolean,
  kFactor: number = 40
): number {
  // Calculate expected scores
  const expectedA = 1 / (1 + Math.pow(10, (ratingB - ratingA) / 400));

  // Actual score (1 for win, 0 for loss)
  const actualA = playerAWon ? 1 : 0;

  // Calculate rating change
  const change = Math.round(kFactor * (actualA - expectedA));

  return change;
}

/**
 * Calculate win probability between two players/teams
 * @param ratingA - ELO rating of player/team A
 * @param ratingB - ELO rating of player/team B
 * @returns Probability (0-1) that player A will win
 */
export function calculateWinProbability(ratingA: number, ratingB: number): number {
  return 1 / (1 + Math.pow(10, (ratingB - ratingA) / 400));
}

/**
 * Calculate dynamic K-factor based on player experience and rating
 * @param rating - Current ELO rating
 * @param matchesPlayed - Number of matches played
 * @param config - Configuration object with dynamic system settings
 * @returns K-factor value
 */
export function calculateDynamicKFactor(
  rating: number,
  matchesPlayed: number,
  config: {
    newPlayerKFactor: number;
    topPlayerKFactor: number;
    advancedPlayerKFactor: number;
    lowerRatedKFactor: number;
    minMatchesForExperience: number;
    topPlayerThreshold: number;
    advancedPlayerThreshold: number;
  }
): number {
  // New players (less than minMatchesForExperience) get higher K-factor for faster adaptation
  if (matchesPlayed < config.minMatchesForExperience) {
    return config.newPlayerKFactor;
  }

  // Experienced players get K-factor based on their rating level
  if (rating >= config.topPlayerThreshold) {
    return config.topPlayerKFactor; // Top players - more stable
  } else if (rating >= config.advancedPlayerThreshold) {
    return config.advancedPlayerKFactor; // Advanced players - balanced
  } else {
    return config.lowerRatedKFactor; // Lower rated players - more responsive
  }
}

/**
 * Calculate ELO change with dynamic K-factor
 * @param ratingA - Current ELO rating of player/team A
 * @param ratingB - Current ELO rating of player/team B
 * @param playerAWon - Whether player/team A won the match
 * @param matchesPlayedA - Number of matches played by team A
 * @param matchesPlayedB - Number of matches played by team B
 * @param config - Configuration object with dynamic system settings
 * @returns ELO change for player A (player B gets the opposite)
 */
export function calculateEloChangeDynamic(
  ratingA: number,
  ratingB: number,
  playerAWon: boolean,
  matchesPlayedA: number,
  matchesPlayedB: number,
  config: {
    newPlayerKFactor: number;
    topPlayerKFactor: number;
    advancedPlayerKFactor: number;
    lowerRatedKFactor: number;
    minMatchesForExperience: number;
    topPlayerThreshold: number;
    advancedPlayerThreshold: number;
  }
): number {
  // Calculate dynamic K-factor for each team
  const kFactorA = calculateDynamicKFactor(ratingA, matchesPlayedA, config);
  const kFactorB = calculateDynamicKFactor(ratingB, matchesPlayedB, config);

  // Use average K-factor for the calculation
  const avgKFactor = (kFactorA + kFactorB) / 2;

  // Calculate expected scores
  const expectedA = 1 / (1 + Math.pow(10, (ratingB - ratingA) / 400));

  // Actual score (1 for win, 0 for loss)
  const actualA = playerAWon ? 1 : 0;

  // Calculate rating change
  const change = Math.round(avgKFactor * (actualA - expectedA));

  return change;
}

/**
 * Get ELO rating tier/category
 * @param rating - ELO rating
 * @param matchesPlayed - Number of matches played (optional)
 * @returns String representing the tier
 */
export function getEloTier(rating: number, matchesPlayed?: number): string {
  // Show "New" for players who haven't played any matches
  if (matchesPlayed !== undefined && matchesPlayed === 0) {
    return "New";
  }

  if (rating >= 2000) return "Elite (8)";
  if (rating >= 1750) return "Expert (7)";
  if (rating >= 1600) return "Advanced (6)";
  if (rating >= 1500) return "Confirmed (5)";
  if (rating >= 1400) return "Intermediate (4)";
  if (rating >= 1300) return "Elementary (3)";
  if (rating >= 1150) return "Improvement (2)";
  return "Beginner (1)";
}
